import React from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { Menu } from 'dig-components/menu';
import { UseMenuTriggerProps } from 'dig-components/hooks';
import { GlobalHeader as DbxGlobalHeader } from 'dig-components/global_header';
import { IconButton } from 'dig-components/buttons';
import { Text, Link } from 'dig-components/typography';
import { UIIcon } from 'dig-components/icons';
import { ListViewLine } from 'dig-components/icons/src';
import HfReactHelper from 'hellosign/components/hf-react-helper';
import { useMatchMedia } from 'hellospa/common/hooks';
import { withRebrandTheme } from 'hello-react/components/rebrand';
import Logo from 'hellosign/components/app/logo-v2';
import { SiteCodeType } from 'hello-react/common/brand';

import AccountMenu, { GlobalHeaderAccountMenuTypes } from './account-menu';
import styles from './global-header.scss';

const messages = defineMessages({
  searchPlaceholder: {
    id: '',
    description: 'Placeholder text for a search input field',
    defaultMessage: 'Search by keyword',
  },
});

declare global {
  interface Window { primarySignatureGuid: string | undefined; }
}

type GlobalHeaderTypes = GlobalHeaderAccountMenuTypes & {
  app: any,
  siteCode: SiteCodeType,
  showUpgradeButton: boolean,

  // Optional
  withSearch?: boolean,
  isBackdropBranding?: boolean,
  onSearch?: () => void,
  searchChips?: React.ReactFragment,
};

function GlobalHeader({
  app,
  siteCode,
  searchChips,
  showUpgradeButton,
  withSearch = false,
  isBackdropBranding = false,
  onSearch,
  ...props
}: GlobalHeaderTypes) {
  const intl = useIntl();
  const isSidebarCollapsed = useMatchMedia('(max-width: 900px)');
  const isWideScreen = useMatchMedia('(min-width: 1441px)');
  const site = HfReactHelper.HfSites.getSite(siteCode);

  // Hack until we get rid of PHP templates.
  const onSidebarToggle = React.useCallback(() => {
    document.body.classList.toggle('responsive-layout__sidebar-open');
  }, []);

  // TODO
  if (withSearch) {
    console.error('Warning! The search bar is not yet fully implemented. Do not use it yet!');
  }

  return (
    <DbxGlobalHeader.Wrapper hasNoBottomBorder={isWideScreen}>
      {isSidebarCollapsed && (
        <IconButton
          type="button"
          variant="transparent"
          onClick={onSidebarToggle}
          data-qa-ref="global-header-sidebar-toggle-btn"
        >
          <UIIcon src={ListViewLine} />
        </IconButton>
      )}
      <Logo
        className={styles.logo}
        siteCode={siteCode}
        variant="wordmark"
        isBackdrop={isBackdropBranding} />
      {withSearch && (
        // Search is not yet fully implemented.
        <DbxGlobalHeader.Search
          hasExpandableAnimation
          onSubmit={onSearch}
          chips={searchChips}
          inputProps={{
            placeholder: intl.formatMessage(messages.searchPlaceholder),
          }} />
      )}
      <DbxGlobalHeader.UtilityNav>
        {/* Help button */}
        <DbxGlobalHeader.Help
          // Need to use `as unknown` hack to get DIG to accept data-qa-ref attr
          triggerProps={{
            'data-qa-ref': 'global-header-help-btn',
          } as unknown as UseMenuTriggerProps}
        >
          <Menu.Segment>
            <Menu.LinkItem
              href="https://faq.hellosign.com"
              data-qa-ref="global-header-faqs-link"
              target="_blank"
            >
              <FormattedMessage
                id=""
                description="Text for a button which redirects the user to the FAQs (frequently asked questions) page when pressed"
                defaultMessage="FAQs" />
            </Menu.LinkItem>
            <Menu.LinkItem
              href={HfReactHelper.urlHelper('home/help', site)}
              data-qa-ref="global-header-contact-us-link"
            >
              <FormattedMessage
                id=""
                description="Text for a button which redirects the user to the Contact Us page when pressed"
                defaultMessage="Contact us" />
            </Menu.LinkItem>
          </Menu.Segment>
        </DbxGlobalHeader.Help>
        <AccountMenu
          app={app}
          siteCode={siteCode}
          showUpgradeButton={showUpgradeButton}
          {...props} />
        {showUpgradeButton && (
          <Text className={styles.upgradeCta}>
            <Link
              href={HfReactHelper.urlHelper('info/pricing', site)}
              variant="primary"
              data-qa-ref="global-header-upgrade-btn"
              rel="nofollow noreferrer"
              target="_blank"
              hasNoUnderline
            >
              <FormattedMessage
                id=""
                description="Text for a button which redirects the user to the Pricing page when pressed"
                defaultMessage="Upgrade" />
            </Link>
          </Text>
        )}
      </DbxGlobalHeader.UtilityNav>
    </DbxGlobalHeader.Wrapper>
  );
}

export default withRebrandTheme(GlobalHeader);
